import { Sliders, PlusCircle, XCircle, Save, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ConfigContent({
  editSiteName, setEditSiteName, editDurations, editDefaultDuration,
  setEditDefaultDuration, editAllowPermanent, setEditAllowPermanent,
  editAllowCustomAddress, setEditAllowCustomAddress,
  editMaintenanceMode, setEditMaintenanceMode,
  editAdminTitle, setEditAdminTitle, addDuration, removeDuration, updateDuration,
  editDomains, addDomain, removeDomain, updateDomain,
  saveSiteSettings, isSavingConfig, primaryDomain
}) {
  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
        <Sliders className="w-5 h-5 text-amber-500" />
        Configuration du site
      </h2>

      <div className="space-y-6">
        {/* Site Name */}
        <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4">
          <label className="text-sm text-zinc-400 mb-2 block">Nom du site</label>
          <Input
            value={editSiteName}
            onChange={(e) => setEditSiteName(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder="ShadowMail"
            data-testid="config-site-name"
          />
        </div>

        {/* Admin Panel Title */}
        <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4">
          <label className="text-sm text-zinc-400 mb-2 block">Titre du panel admin</label>
          <Input
            value={editAdminTitle}
            onChange={(e) => setEditAdminTitle(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder="Admin"
            data-testid="config-admin-title"
          />
        </div>

        {/* Durations */}
        <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-zinc-400">Durées disponibles</label>
            <Button
              onClick={addDuration}
              variant="ghost"
              size="sm"
              className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 h-8 gap-1 text-xs"
              data-testid="add-duration-btn"
            >
              <PlusCircle className="w-3.5 h-3.5" />
              Ajouter
            </Button>
          </div>

          <div className="space-y-2">
            {editDurations.map((dur, index) => (
              <div key={index} className={`bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-3 transition-opacity ${dur.enabled === false ? 'opacity-50' : ''}`} data-testid={`duration-row-${index}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white font-medium truncate">{dur.label || dur.key || "Nouvelle durée"}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => updateDuration(index, "enabled", dur.enabled === false)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${dur.enabled !== false ? 'bg-green-500' : 'bg-zinc-700'}`}
                      title={dur.enabled !== false ? "Visible" : "Masqué"}
                    >
                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${dur.enabled !== false ? 'translate-x-5' : ''}`} />
                    </button>
                    <Button
                      onClick={() => removeDuration(index)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={dur.key}
                    onChange={(e) => updateDuration(index, "key", e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white w-20 text-xs flex-shrink-0 h-8"
                    placeholder="ex: 1h"
                  />
                  <Input
                    value={dur.label}
                    onChange={(e) => updateDuration(index, "label", e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white flex-1 min-w-0 max-w-[120px] sm:max-w-none text-xs h-8"
                    placeholder="ex: 1 heure"
                  />
                  <Input
                    type="number"
                    value={dur.minutes}
                    onChange={(e) => updateDuration(index, "minutes", e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white w-16 text-xs flex-shrink-0 h-8"
                    placeholder="min"
                  />
                  <span className="text-zinc-600 text-xs flex-shrink-0">min</span>
                </div>
              </div>
            ))}
            {editDurations.length === 0 && (
              <p className="text-zinc-600 text-xs text-center py-2">Aucune durée configurée</p>
            )}
          </div>
        </div>

        {/* Default Duration */}
        <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4">
          <label className="text-sm text-zinc-400 mb-2 block">Durée par défaut</label>
          <Select value={editDefaultDuration} onValueChange={setEditDefaultDuration}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="config-default-duration">
              <SelectValue placeholder="Sélectionnez..." />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              {editDurations.filter(d => d.key && d.key.trim()).map((dur) => (
                <SelectItem key={dur.key} value={dur.key} className="text-xs">
                  {dur.label || dur.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Toggle options */}
        <ToggleOption
          label='Option "Permanent"'
          description="Permettre aux utilisateurs de créer des emails permanents"
          enabled={editAllowPermanent}
          onToggle={() => setEditAllowPermanent(!editAllowPermanent)}
          color="amber"
          testId="toggle-permanent"
        />

        <ToggleOption
          label="Adresse personnalisée"
          description="Permettre aux utilisateurs de choisir leur propre adresse email"
          enabled={editAllowCustomAddress}
          onToggle={() => setEditAllowCustomAddress(!editAllowCustomAddress)}
          color="cyan"
          testId="toggle-custom-address"
        />

        <div className={`border rounded-lg p-4 ${editMaintenanceMode ? 'bg-red-950/30 border-red-500/50' : 'bg-zinc-900 border-zinc-800'}`}>
          <div className="flex items-center justify-between">
            <div>
              <label className={`text-sm block ${editMaintenanceMode ? 'text-red-400 font-medium' : 'text-zinc-400'}`}>Mode maintenance</label>
              <p className="text-xs text-zinc-600 mt-1">Afficher une page de maintenance sur le site public</p>
            </div>
            <button
              onClick={() => setEditMaintenanceMode(!editMaintenanceMode)}
              className={`relative w-12 h-6 rounded-full transition-colors ${editMaintenanceMode ? 'bg-red-500' : 'bg-zinc-700'}`}
              data-testid="toggle-maintenance"
            >
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${editMaintenanceMode ? 'translate-x-6' : ''}`} />
            </button>
          </div>
        </div>

        {/* Domains */}
        <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-zinc-400 flex items-center gap-2">
              <Globe className="w-4 h-4 text-cyan-400" />
              Domaines email
            </label>
            <Button
              onClick={addDomain}
              variant="ghost"
              size="sm"
              className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 h-8 gap-1 text-xs"
            >
              <PlusCircle className="w-3.5 h-3.5" />
              Ajouter
            </Button>
          </div>
          <div className="space-y-2">
            {/* Default domain - always present, read-only */}
            <div className="flex items-center gap-2">
              <span className="text-zinc-600 text-sm">@</span>
              <Input
                value={primaryDomain}
                disabled
                className="bg-zinc-800/50 border-zinc-700/50 text-zinc-500 flex-1 text-sm font-mono cursor-not-allowed"
              />
              <span className="text-xs text-amber-400 whitespace-nowrap">principal</span>
              <div className="w-8 flex-shrink-0" />
            </div>
            {editDomains.map((domain, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="text-zinc-500 text-sm">@</span>
                <Input
                  value={domain}
                  onChange={(e) => updateDomain(index, e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white flex-1 text-sm font-mono"
                  placeholder="example.com"
                />
                <Button
                  onClick={() => removeDomain(index)}
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0 flex-shrink-0"
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-600 mt-2">{primaryDomain} est le domaine par défaut</p>
        </div>

        {/* Save */}
        <Button
          onClick={saveSiteSettings}
          disabled={isSavingConfig}
          className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium"
          data-testid="save-config-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {isSavingConfig ? "Sauvegarde..." : "Sauvegarder la configuration"}
        </Button>
      </div>
    </div>
  );
}

function ToggleOption({ label, description, enabled, onToggle, color, testId }) {
  const colorMap = {
    amber: { bg: 'bg-amber-500', border: '' },
    cyan: { bg: 'bg-cyan-500', border: '' },
  };
  const c = colorMap[color] || colorMap.amber;

  return (
    <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm text-zinc-400 block">{label}</label>
          <p className="text-xs text-zinc-600 mt-1">{description}</p>
        </div>
        <button
          onClick={onToggle}
          className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? c.bg : 'bg-zinc-700'}`}
          data-testid={testId}
        >
          <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : ''}`} />
        </button>
      </div>
    </div>
  );
}
